import ExpandableText from '@/components/ExpandableText';
import InformationTextsModal from '@/components/InformationTextsModal';
import TicketCuticle from '@/components/manage/TicketCuticle';
import NonTripOfferValidity from '@/components/manage/travel-passes/NonTripOfferValidity';
import SendTravelPassToEmailModal from '@/components/manage/travel-passes/SendTravelPassToEmailModal';
import TravelPassDownload from '@/components/manage/travel-passes/TravelPassDownload';
import ViewBookedTripsDialog from '@/components/manage/travel-passes/ViewBookedTripsDialog';
import PassengerBookingAttributes from '@/components/passenger/PassengerBookingAttributes';
import PriceDisplay from '@/components/PriceDisplay';
import Changeability from '@/components/purchase/card/Changeability';
import Refundability from '@/components/purchase/card/Refundability';
import QRCode from '@/components/QRCode';
import { manageFlowBookingSelector } from '@/features/manage/manageSelectors';
import { TransText } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import type {
  MultiRideAccountDTO,
  TravelPassAccountDTO,
} from '@/types/dto/agent';
import type { DecoratedTravelPassDetails } from '@/types/travelpass';
import { buildPassengerTypeByPassengerIdFromBooking } from '@/utils/booking';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { isTimestampInPast } from '@/utils/dateTime';
import {
  Badge,
  Button,
  Divider,
  Icons,
  Typography,
} from '@packages/webshop-design-system';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

interface TravelPassDetailsProps {
  data: DecoratedTravelPassDetails<MultiRideAccountDTO | TravelPassAccountDTO>;
}

const TravelPassDetails: FC<TravelPassDetailsProps> = ({ data }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSendToEmailModalOpen, setIsSendToEmailModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const zone =
    data.travelAccount?.regionalConstraint?.regionalValidities[0].zone;
  const origin = zone?.entryStation?.name;
  const destination = zone?.terminalStation?.name;
  const booking = useSelector(manageFlowBookingSelector);
  const passengerId = data.passengerIds?.[0];
  const passengerCard = booking?.passengers?.find((y) => y.id === passengerId);
  const passengerTypeByPassengerId = useMemo(
    () => buildPassengerTypeByPassengerIdFromBooking(booking),
    [booking]
  );
  const isRefunded = data.status === 'REFUNDED';
  const isExpired = data.validUntil
    ? isTimestampInPast(data.validUntil, 'Europe/Stockholm')
    : false;

  // Extract fulfillment ID from booking for this travel pass
  const getFulfillmentId = (): string | undefined => {
    if (!booking?.nonTripOffers) return undefined;

    for (const offer of booking.nonTripOffers) {
      for (const admission of offer.admissions || []) {
        if (admission.id === data.id) {
          return admission.fulfillments?.[0]?.id;
        }
      }
    }

    return undefined;
  };

  const fulfillmentId = getFulfillmentId();

  const getHolderName = (): string => {
    const holder = data.travelAccount?.holder;
    if (holder?.firstName && holder?.lastName) {
      return `${holder.firstName} ${holder.lastName}`;
    }

    const purchaser = booking?.purchaser;
    return `${purchaser?.firstName} ${purchaser?.lastName}`;
  };

  const holderName = getHolderName();

  const renderPassengerInfo = () => (
    <div className="flex flex-col">
      <Typography variant="body1-bold">
        {data.travelAccount?.holder?.firstName}
        &nbsp;
        {data.travelAccount?.holder?.lastName}
      </Typography>
      <PassengerBookingAttributes
        passenger={passengerCard}
        passengerType={
          passengerId ? passengerTypeByPassengerId[passengerId] : undefined
        }
      />
    </div>
  );

  const renderPrice = () => (
    <div data-testid="travel-pass-price">
      <Typography variant="body1-bold" className="text-primary">
        <PriceDisplay amount={data.price?.amount} />
      </Typography>
    </div>
  );

  // A travel pass carries a single long description. "Read more" opens it in
  // the information modal instead of expanding inline, matching the sales flow
  // and the ticket details (BR-57160). Rendered in both the desktop and mobile
  // branches below, so keep the wiring in one place.
  const descriptions = data?.longDescription ? [data.longDescription] : [];

  const renderDescription = () => (
    <ExpandableText
      text={descriptions[0]}
      renderHtml
      onReadMore={() => setIsInfoModalOpen(true)}
    />
  );

  const renderRouteInfo = () => (
    <div
      className={cn(
        'border-b border-b-primary bg-primary-pale text-primary laptop:mr-1 laptop:rounded-tr-lg',
        {
          'border-b-neutral-light bg-neutral-pale': isRefunded,
        }
      )}
    >
      <div className="flex flex-row flex-wrap items-center px-4 py-3">
        <Typography
          variant="subtitle"
          className={cn('min-w-fit', {
            'text-neutral': isRefunded,
          })}
        >
          {origin}
        </Typography>
        <Icons.arrows
          className={cn('mx-1.5 h-4 w-4 shrink-0', {
            'text-neutral': isRefunded,
          })}
        />
        <Typography
          variant="subtitle"
          className={cn('min-w-fit', {
            'text-neutral': isRefunded,
          })}
        >
          {destination}
        </Typography>
      </div>
    </div>
  );

  const renderRefundBadge = () => (
    <Badge variant="disabled" className="h-5">
      <Icons.ticketReturn height={16} width={16} />
      <TransText i18nKey="refunded" />
    </Badge>
  );

  return (
    <>
      <div
        className={cn(
          'flex overflow-hidden rounded-lg bg-background bg-white text-dark',
          isLaptopOrBigger ? 'flex-row' : 'flex-col',
          { 'bg-neutral-pale': isRefunded }
        )}
      >
        <div
          className={cn('flex w-1/2 flex-col', {
            'w-full rounded-t-lg border-x border-t border-neutral-light':
              !isLaptopOrBigger,
            'rounded-l-lg border-b border-l border-t border-neutral-light':
              isLaptopOrBigger,
          })}
        >
          {renderRouteInfo()}
          <div className="flex flex-row justify-between p-4">
            <div className="flex flex-col gap-4">
              <div className="flex flex-row items-center gap-1">
                <Typography variant="body2" className="text-neutral">
                  <TransText i18nKey="travelPass" />:
                </Typography>
                <Typography
                  variant="body2"
                  className={cn({
                    'text-neutral': isRefunded,
                  })}
                >
                  {data.travelAccount?.number}
                </Typography>
              </div>
              <div>
                <NonTripOfferValidity data={data} isRefunded={isRefunded} />
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => setIsDialogOpen(true)}
                  className="gap-1"
                  data-testid="view-booked-trips-button"
                >
                  <Icons.alertCircle className="h-4 w-4" />
                  <TransText i18nKey="viewBookedTrips" />
                </Button>
              </div>
              <div className="flex flex-row items-center gap-3">
                <Changeability condition={data.exchangeable === 'YES'} />
                <Refundability condition={data.refundable === 'YES'} />
              </div>
            </div>
            {data.controlNumber && (
              <QRCode controlNumber={data.controlNumber} />
            )}
          </div>
        </div>
        {isLaptopOrBigger ? (
          <TicketCuticle isVertical />
        ) : (
          <Divider
            variant="medium"
            className="mx-4 w-auto border-l-2 border-neutral-light"
          />
        )}
        {isLaptopOrBigger ? (
          <div className="flex w-1/2 flex-col rounded-r-lg border-b border-r border-t border-neutral-light px-2 py-3">
            <div className="flex h-full flex-col">
              <div className="flex flex-grow flex-col gap-3 px-4">
                <Typography
                  variant="subtitle"
                  className={cn({
                    'text-neutral': isRefunded,
                  })}
                  as="h2"
                >
                  {data?.shortDescription}
                </Typography>
                {renderDescription()}
              </div>
              <Divider
                variant="medium"
                className="mx-4 my-3 w-auto flex-grow-0"
              />
              <div className="flex flex-row items-center justify-between px-4">
                {renderPassengerInfo()}
                {isRefunded ? renderRefundBadge() : renderPrice()}
              </div>
              {!isRefunded && !isExpired && (
                <div className="mt-1 flex gap-2 px-3 pt-3">
                  <Button
                    variant="secondary"
                    size="regular"
                    onClick={() => setIsSendToEmailModalOpen(true)}
                    data-testid="travel-pass-send-to-email"
                    className="gap-1.5"
                  >
                    <Icons.email height={20} width={20} />
                    <TransText i18nKey="sendToEmail" />
                  </Button>
                  <TravelPassDownload
                    fulfillmentId={fulfillmentId}
                    bookingId={booking?.id}
                    bookingCode={booking?.code}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="flex w-full flex-col border-x border-neutral-light pt-4">
              <div className="flex flex-grow flex-col gap-3 px-4">
                <Typography
                  variant="subtitle"
                  className={cn({
                    'text-neutral': isRefunded,
                  })}
                  as="h2"
                >
                  {data?.shortDescription}
                </Typography>
                {renderDescription()}
              </div>
            </div>
            <div className="relative w-full py-3">
              <TicketCuticle isVertical={false} />
            </div>
            <div className="flex w-full flex-col rounded-b-lg border-x border-b border-neutral-light">
              <div className="flex flex-row items-center justify-between px-4">
                {renderPassengerInfo()}
                {isRefunded ? renderRefundBadge() : renderPrice()}
              </div>
              {!isRefunded && !isExpired && (
                <div className="mt-1 flex gap-2 p-3">
                  <Button
                    variant="secondary"
                    size="large"
                    onClick={() => setIsSendToEmailModalOpen(true)}
                    data-testid="travel-pass-send-to-email"
                    className="gap-1.5"
                  >
                    <Icons.email height={20} width={20} />
                    <TransText i18nKey="sendToEmail" />
                  </Button>
                  <TravelPassDownload
                    fulfillmentId={fulfillmentId}
                    bookingId={booking?.id}
                    bookingCode={booking?.code}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ViewBookedTripsDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        consumptions={data.travelAccount?.consumptions ?? []}
      />

      <SendTravelPassToEmailModal
        isOpen={isSendToEmailModalOpen}
        onOpenChange={setIsSendToEmailModalOpen}
        holderName={holderName}
        defaultEmail={
          data.travelAccount?.holder?.email ||
          booking?.purchaser?.contactInformation?.emailAddress ||
          ''
        }
      />

      <InformationTextsModal
        title={data?.shortDescription}
        // Matches renderPrice(), which the refunded branch replaces with a
        // badge; `amount` is optional so the modal omits it when refunded.
        amount={isRefunded ? undefined : data.price?.amount}
        descriptions={descriptions}
        isOpen={isInfoModalOpen}
        onOpenChange={setIsInfoModalOpen}
        data-testid="travel-pass-information-modal"
      />
    </>
  );
};

export default TravelPassDetails;
